import axios from "axios";
import { useCallback, useContext, useMemo } from "react";
import { TeamsFxContext } from "../components/teams/TeamsFxContext";
import { LocalStorageKeys } from "../types";
import { getAuth, User } from "firebase/auth";
import * as Sentry from "@sentry/react";
import { stubAdapter } from "../stubs/stub-adapter";

const useGetTeamsToken = () => {
  const teamsUserCredential = useContext(TeamsFxContext).teamsUserCredential;

  return useCallback(async () => {
    if (!teamsUserCredential) {
      return undefined;
    }

    // Ignore errors for anonymous users
    try {
      return (await teamsUserCredential.getToken(""))!.token;
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
    }
  }, [teamsUserCredential]);
};

export const useAxios = () => {
  const getFirebaseToken = async (
    refresh?: boolean
  ): Promise<string | undefined> => {
    const auth = getAuth();
    const firebaseUser: User | null =
      auth.currentUser ||
      (await new Promise((resolve) => {
        const unsubscribe = auth.onAuthStateChanged((currentUser) => {
          if (!localStorage.getItem(LocalStorageKeys.EXPECT_LOGIN)) {
            unsubscribe();
            console.debug("No firebase login expected...");
            return resolve(currentUser);
          }
          if (currentUser) {
            unsubscribe();
            console.debug("Firebase login expected, resolve current user...");
            return resolve(currentUser);
          }
        });
      }));
    const token = firebaseUser?.getIdToken(refresh);
    return token;
  };

  const getTeamsToken = useGetTeamsToken();

  return useMemo(() => {
    // In stub mode, use the stub adapter so no real network requests are made
    if (import.meta.env.VITE_STUB) {
      return axios.create({ adapter: stubAdapter });
    }

    const instance = axios.create();

    // Set up the interceptor for this instance
    instance.interceptors.request.use(async (config) => {
      const token =
        (await getTeamsToken()) ||
        localStorage.getItem(LocalStorageKeys.LANGFINITY_TOKEN) ||
        (await getFirebaseToken());

      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    });

    return instance;
  }, [getTeamsToken]);
};
